import { useCallback } from "react";
import type {
  EventId,
  PlanEvent,
  Category,
} from "../../../shared/types";
import type { EventFullData, UpdateEventData } from "../../events/types/event-form-types";
import type { CalendarNavigationState } from "./use-calendar-navigation";
import type { FormAnchor, PendingRecurrence } from "./use-week-calendar-state";
import { useUpdateEventHandler } from "./use-update-event-handler";

interface UseEventHandlersParams {
  readonly events: PlanEvent[];
  readonly nav: CalendarNavigationState;
  readonly setShowAddForm: (show: boolean) => void;
  readonly setFormStartTime: (time: string | null) => void;
  readonly setFormEndTime: (time: string | null) => void;
  readonly setFormAnchor: (anchor: FormAnchor | null) => void;
  readonly setEditingEvent: (event: PlanEvent | null) => void;
  readonly setShowFullEditForm: (show: boolean) => void;
  readonly selectedEvents: readonly PlanEvent[];
  readonly setPendingRecurrence?: (pending: PendingRecurrence | null) => void;
  readonly setPendingResync?: (event: PlanEvent | null) => void;
  readonly onAddEvent?: (
    title: string,
    category: Category,
    date: string,
    startTime: string,
    endTime: string,
  ) => void;
  readonly onAddEventFull?: (data: EventFullData) => void;
  readonly onDeleteEvent?: (id: EventId) => void;
  readonly onAddRecurrenceException?: (baseId: EventId, date: string) => void;
  readonly onResyncOccurrence?: (
    baseId: EventId,
    date: string,
    detachedId: EventId,
  ) => void;
  readonly onResyncAllOccurrences?: (baseId: EventId) => void;
  readonly onUpdateEvent?: (id: EventId, updates: UpdateEventData) => void;
}

export function useEventHandlers({
  events,
  nav,
  setShowAddForm,
  setFormStartTime,
  setFormEndTime,
  setFormAnchor,
  setEditingEvent,
  setShowFullEditForm,
  setPendingRecurrence,
  onAddEventFull,
  onUpdateEvent,
}: UseEventHandlersParams) {
  const handleEventClick = useCallback(
    (event: PlanEvent, anchorRect?: DOMRect) => {
      nav.setSelectedDate(event.date);
      setEditingEvent(event);
      // Sur mobile, ouvrir directement le formulaire complet (pas de vue rapide)
      const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
      if (isMobile) {
        setShowFullEditForm(true);
      } else if (anchorRect) {
        setFormAnchor({
          colRight: anchorRect.right,
          colLeft: anchorRect.left,
          slotTop: anchorRect.top,
        });
      }
      setShowAddForm(false);
    },
    [nav, setEditingEvent, setFormAnchor, setShowAddForm, setShowFullEditForm],
  );

  const handleEventLongPress = useCallback(
    (event: PlanEvent, anchorRect: DOMRect) => {
      nav.setSelectedDate(event.date);
      setEditingEvent(event);
      setFormAnchor({
        colRight: anchorRect.right,
        colLeft: anchorRect.left,
        slotTop: anchorRect.top,
      });
      setShowFullEditForm(true);
      setShowAddForm(false);
    },
    [nav, setEditingEvent, setFormAnchor, setShowFullEditForm, setShowAddForm],
  );

  const handleSlotClick = useCallback(
    (date: string, startTime: string, endTime: string, anchor?: FormAnchor) => {
      nav.setSelectedDate(date);
      setFormStartTime(startTime);
      setFormEndTime(endTime);
      setFormAnchor(anchor ?? null);
      setShowAddForm(true);
      setEditingEvent(null);
    },
    [nav, setFormStartTime, setFormEndTime, setFormAnchor, setShowAddForm, setEditingEvent],
  );

  const handleMobileSlotClick = useCallback(
    (startTime: string, endTime: string) => {
      const date = nav.selectedDate ?? nav.currentDate;
      if (date) {
        setFormStartTime(startTime);
        setFormEndTime(endTime);
        setShowAddForm(true);
      }
    },
    [nav.selectedDate, nav.currentDate, setFormStartTime, setFormEndTime, setShowAddForm],
  );

  const closeAddForm = useCallback(() => {
    setShowAddForm(false);
    setFormStartTime(null);
    setFormEndTime(null);
    setFormAnchor(null);
  }, [setShowAddForm, setFormStartTime, setFormEndTime, setFormAnchor]);

  const handleOpenAdd = useCallback(() => {
    if (!nav.selectedDate) {
      nav.setSelectedDate(nav.currentDate);
    }
    setShowAddForm(true);
    setEditingEvent(null);
    setShowFullEditForm(false);
    setFormAnchor(null);
  }, [nav, setShowAddForm, setEditingEvent, setShowFullEditForm, setFormAnchor]);

  const handleDuplicate = useCallback(
    (event: PlanEvent) => {
      if (!onAddEventFull) return;
      onAddEventFull({
        title: `${event.title} (copie)`,
        category: event.category,
        date: event.date,
        startTime: event.startTime,
        endTime: event.endTime,
        description: event.description,
        notes: event.notes,
        tags: [...event.tags],
        recurrence: event.recurrence,
        reminderMinutes: event.reminderMinutes,
        reminderTime: event.reminderTime ?? null,
        reminderType: event.reminderType ?? null,
      });
    },
    [onAddEventFull],
  );

  const { handleUpdateEvent } = useUpdateEventHandler({
    events,
    setPendingRecurrence,
    onUpdateEvent,
  });

  return {
    handleEventClick,
    handleEventLongPress,
    handleSlotClick,
    handleMobileSlotClick,
    closeAddForm,
    handleOpenAdd,
    handleDuplicate,
    handleUpdateEvent,
  };
}
